/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable max-statements */

// @ts-ignore Není to ESM.
import ICAL from 'ical.js';

import {
  MagazinePlaylist,
  Playlist,
  ShufflePlaylist,
  Track
} from '@ki-wi/deterministic';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';

import IcalPlanner from '../../ical/ical-planner';
import ScheduleEnum from '../../schedule/schedule-enum';

/**
 * @import {
 *   ClipIterator,
 *   ClipIteratorFactory,
 *   Duration,
 *   KeyedClipIteratorFactory,
 *   Time,
 *   Validator as TrackValidator
 * } from '@ki-wi/deterministic'
 * @import { SpotContent } from '../../content/controller'
 * @import { Playlist as PlaylistModel } from '../../playlist/playlist'
 * @import { Track as TrackModel } from '../../track/track'
 * @import { Content } from '../content'
 */

/**
 * @typedef {Record<'excludeUnknownClipDuration', boolean>} ExcludeUnknownClipDurationRecord
 * @typedef {KeyedClipIteratorFactory<SpotContent, string>} IIteratorFactory TODO: Správně by mělo být SpotContent | IIteratorFactory.
 */

// TODO: Podle konvencí ostatních iCal věcí by tato třída měla mít závilost na ICAL.
const icalPlanner = new IcalPlanner(ICAL);

/**
 * Třída usnadňující mapování na datové modely vhodné pro Simulator.
 * @implements {IIteratorFactory}
 */
export class IteratorFactory {
  /**
   * Vytvoří novou instanci.
   * @param {string} id Identifikátor playlistu.
   * @param {ClipIteratorFactory<SpotContent | IteratorFactory>} itf Iterátor
   */
  constructor(id, itf) {
    this.id = id;
    this.itf = itf;
  }

  /** @type {IIteratorFactory['global']} */
  get global() {
    return this.itf.global;
  }

  /** @type {IIteratorFactory['clone']} */
  clone() {
    const itf = this.itf.clone();
    const keyedClipIteratorFactory = new IteratorFactory(this.id, itf);
    return keyedClipIteratorFactory;
  }

  /** @type {IIteratorFactory['key']} */
  key() {
    return this.id;
  }

  /** @type {IIteratorFactory['newIterator']} */
  newIterator(t) {
    return /** @type {ClipIterator<SpotContent>} TODO: Dočasné přetypování. */ (
      this.itf.newIterator(t)
    );
  }

  /** @type {IIteratorFactory['valid']} */
  valid(t) {
    return this.itf.valid(t);
  }

  /** @type {IIteratorFactory['currentOrNextValid']} */
  currentOrNextValid(t, maxTime) {
    return this.itf.currentOrNextValid(t, maxTime);
  }

  /**
   * Funkce která namapuje Playlist na IteratorFactory.
   * @param {PlaylistModel} playlist Playlist, který se bude mapovat.
   * @param {number} volume
   * @param {ExcludeUnknownClipDurationRecord} config
   * @returns {IteratorFactory} Namapovaný playlist do KeyedClipIteratorFactory.
   */
  static fromPlaylist(playlist, volume, config) {
    const { id, scope, cut, scheduleEnum } = playlist;
    const tracks = playlist.tracks
      .map((t) => IteratorFactory.track(t, volume, config))
      .filter((t) => !isNil(t));

    /** @type {ClipIteratorFactory<SpotContent | IteratorFactory> | undefined} */
    let p;
    switch (scheduleEnum) {
      case ScheduleEnum.RANDOM:
        p = new ShufflePlaylist(...tracks);
        break;
      case ScheduleEnum.STATIC:
      case ScheduleEnum.DYNAMIC:
      default:
        p = new Playlist(...tracks);
        break;
    }

    let clipIteratorFactory = p;
    if (scope === 'application' && cut > 0) {
      clipIteratorFactory = new MagazinePlaylist(cut, p);
    }
    return new IteratorFactory(id, clipIteratorFactory);
  }

  /**
   * Funkce, která převede PlayerLighter Track model na model vhodný pro Simulator.
   * @param {TrackModel} track PlayerLighter model.
   * @param {number} parentVolume
   * @param {ExcludeUnknownClipDurationRecord} config
   * @returns {Track<SpotContent | IteratorFactory> | undefined} Model tracku pro Simulator nebo `undefined`, pokud se tento track nemá brát v úvahu.
   */
  static track(track, parentVolume, config) {
    /** @type {TrackValidator | null} */
    let validator = null;
    if (!isNil(track.planIcal)) {
      validator = IteratorFactory.validator(track);
    }

    let contentDuration = track.playDuration;
    if (track.medium) {
      // Pokud se jedná o médium a duration není známá, tak se pokusíme převzít
      // "fyzickou" délku z metadat (to lze jen pro file média).
      const { medium } = track;
      if (contentDuration === 0 && isNumber(medium?.params?.physicalDuration)) {
        contentDuration = medium.params.physicalDuration * 1000;
      }
    }
    if (contentDuration < 0) {
      throw new Error('contentDuration must be non-negative');
    }

    const contentVolume = track.volume * parentVolume;

    if (isClipIteratorFactory(track)) {
      const iteratorFactory = IteratorFactory.fromPlaylist(
        track.playlist,
        contentVolume,
        config
      );
      return new Track(
        iteratorFactory,
        /** @type {Duration} */ (contentDuration * 1000),
        contentVolume,
        validator
      );
    }

    // Pokud zapnuta volba excludeUnknownClipDuration a contentDuration je 0,
    // tak se takový track nesmí použít při přehrávání.
    if (contentDuration === 0 && config.excludeUnknownClipDuration) {
      return undefined;
    }

    return trackFromContent(track, contentDuration, validator, contentVolume);
  }

  /**
   * Funkce, která vytvoří validator který určí zda je track platný,
   * nebo kdy bude platný.
   * @param {TrackModel} track Track pro který se má zjišťovat validita.
   * @returns {TrackValidator} Validátor pro track.
   */
  static validator(track) {
    return {
      valid: (t) =>
        isNil(track.planIcal) ||
        icalPlanner.hasEventOnDate(track.planIcal, new Date(t / 1000)),
      currentOrNextValid: (t, maxTime) => {
        if (isNil(track.planIcal)) {
          return t;
        }
        const date = icalPlanner.currentOrNextValid(
          track.planIcal,
          new Date(t / 1000),
          new Date(maxTime / 1000)
        );
        if (isNil(date)) {
          return maxTime;
        }

        return /** @type {Time} */ (date.getTime() * 1000);
      }
    };
  }
}

/**
 * @param {Content} content
 * @param {number} duration
 * @param {TrackValidator | null} validator
 * @param {number} volume
 * @returns {Track<SpotContent | IteratorFactory>}
 */
function trackFromContent(content, duration, validator, volume) {
  return new Track(
    { model: content },
    /** @type {Duration} */ (duration * 1000),
    volume,
    validator
  );
}

/**
 * Metoda vrací true, pokud track obsahuje playlist, který není deterministický.
 * @param {TrackModel} track
 * @returns {track is TrackModel & { playlist: { deterministic: false } }}
 */
function isClipIteratorFactory(track) {
  return track.playlist?.deterministic === false;
}

export const internal = { trackFromContent };
